/**
 * Common frame for every Service Desk page: the access check, the page header,
 * and the Queue / Knowledge base / Settings tab links. The api enforces the
 * `it.servicedesk` grant too; this keeps a person without it from seeing a
 * page full of failed requests.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { BackLink, PageHeader, StateCard } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { hasServiceDeskAccess } from '@/lib/useServiceDesk';
import { SD_MUTED } from './sdStyles';

export type SdTab = 'queue' | 'kb' | 'settings';

const TABS: { id: SdTab; label: string; to: string }[] = [
  { id: 'queue', label: 'Queue', to: '/it/service-desk' },
  { id: 'kb', label: 'Knowledge base', to: '/it/service-desk/kb' },
  { id: 'settings', label: 'Settings', to: '/it/service-desk/settings' },
];

export function SdTabs({ active, aside }: { active: SdTab; aside?: ReactNode }) {
  return (
    <nav
      aria-label="Service Desk sections"
      className="mb-5 flex flex-wrap gap-1 border-b border-amp-line dark:border-amp-dark-line"
    >
      {TABS.map((t) => (
        <Link
          key={t.id}
          to={t.to}
          aria-current={t.id === active ? 'page' : undefined}
          className={
            t.id === active
              ? '-mb-px border-b-2 border-amp-olive-deep px-3 py-2 text-sm font-semibold text-amp-ink dark:border-amp-olive dark:text-amp-dark-ink'
              : 'px-3 py-2 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink'
          }
        >
          {t.label}
        </Link>
      ))}
      {aside && <div className="ml-auto flex items-center self-center pb-1">{aside}</div>}
    </nav>
  );
}

export function SdShell({
  title,
  subtitle,
  tab,
  back,
  actions,
  tabsAside,
  children,
}: {
  title: string;
  subtitle?: string;
  /** Which section tab is active. Omit on pages that show a back link instead. */
  tab?: SdTab;
  back?: { to: string; label: string };
  actions?: ReactNode;
  /** Small controls kept on the right of the tab row (the queue's summary toggle). */
  tabsAside?: ReactNode;
  children: ReactNode;
}) {
  const { user, isLoading } = useSession();
  const allowed = hasServiceDeskAccess(user?.permissions, user?.superAdmin);

  if (!isLoading && user && !allowed) {
    return (
      <StateCard
        title="You do not have access to the Service Desk"
        description="Ask IT for the Service Desk permission if you need to work tickets. You can still open a request from the Help button at the top of the page."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {back && (
        <BackLink as={Link} to={back.to} className="mb-3">
          {back.label}
        </BackLink>
      )}
      <PageHeader eyebrow="IT - Service Desk" title={title} subtitle={subtitle} actions={actions} />
      {tab && <SdTabs active={tab} aside={tabsAside} />}
      {isLoading ? <p className={`text-sm ${SD_MUTED}`}>Loading...</p> : children}
    </div>
  );
}

export function SdError({ error, what }: { error: unknown; what: string }) {
  if (!error) return null;
  const message = error instanceof Error ? error.message : String(error);
  return (
    <p className="mt-2 text-[13px] text-red-700 dark:text-red-300">
      Could not {what}: {message}
    </p>
  );
}
